/**
 * @spec docs/specs/features/auto-whitelist-privy-wallets.md
 *
 * Hook for automatic bridge allowlist registration.
 * After Privy authentication, silently signs an EIP-712 RegisterForBridge
 * message and registers the user's embedded wallet with the bridge relayer.
 *
 * Non-blocking: errors are logged but don't crash the app.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { checkBridgeAllowlist } from '@/lib/swap-api';
import { registerForBridge } from '@/lib/bridge-registration-api';

/** EIP-712 domain for bridge registration (must match bridge relayer) */
const BRIDGE_REGISTER_DOMAIN = {
  name: 'PHNM Bridge',
  version: '1',
} as const;

/** EIP-712 types for RegisterForBridge (must match bridge relayer) */
const REGISTER_FOR_BRIDGE_TYPES = {
  RegisterForBridge: [
    { name: 'wallet', type: 'address' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

export interface UseBridgeRegistrationReturn {
  isAllowlisted: boolean;
  isRegistering: boolean;
  error: string | null;
}

export function useBridgeRegistration(): UseBridgeRegistrationReturn {
  const { authenticated, ready, getAccessToken } = usePrivy();
  const { wallets } = useWallets();
  const queryClient = useQueryClient();
  const registeredAddress = useRef<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find Privy embedded wallet
  const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
  const embeddedAddress = embeddedWallet?.address ?? null;

  // Check allowlist status (only when authenticated with embedded wallet)
  const { data: isAllowlisted = false } = useQuery({
    queryKey: ['bridge-allowlist', embeddedAddress],
    queryFn: () => checkBridgeAllowlist(embeddedAddress!),
    enabled: !!embeddedAddress && authenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const doRegistration = useCallback(async () => {
    if (!embeddedWallet || !embeddedAddress) return;

    setIsRegistering(true);
    setError(null);

    try {
      // Get Privy access token
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }

      // Get the Ethereum provider from the embedded wallet
      const provider = await embeddedWallet.getEthereumProvider();

      // Sign EIP-712 RegisterForBridge message
      const timestamp = Math.floor(Date.now() / 1000);

      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
          ],
          ...REGISTER_FOR_BRIDGE_TYPES,
        },
        primaryType: 'RegisterForBridge',
        domain: BRIDGE_REGISTER_DOMAIN,
        message: {
          wallet: embeddedAddress,
          timestamp,
        },
      };

      const signature = await provider.request({
        method: 'eth_signTypedData_v4',
        params: [embeddedAddress, JSON.stringify(typedData)],
      });

      // Call registration endpoint
      await registerForBridge(embeddedAddress, timestamp, signature as string, accessToken);

      // Invalidate allowlist query to refresh
      await queryClient.invalidateQueries({ queryKey: ['bridge-allowlist', embeddedAddress] });

      console.info('[bridge-registration] Registration successful');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      console.warn('[bridge-registration] Registration failed:', message);
    } finally {
      setIsRegistering(false);
    }
  }, [embeddedWallet, embeddedAddress, getAccessToken, queryClient]);

  // Trigger registration when conditions are met
  useEffect(() => {
    if (
      !ready ||
      !authenticated ||
      !embeddedAddress ||
      isAllowlisted ||
      isRegistering ||
      registeredAddress.current === embeddedAddress
    ) {
      return;
    }

    registeredAddress.current = embeddedAddress;
    void doRegistration();
  }, [ready, authenticated, embeddedAddress, isAllowlisted, isRegistering, doRegistration]);

  return {
    isAllowlisted,
    isRegistering,
    error,
  };
}
